// packages/core/src/quizzes/content/incomeOrLeaks.ts
// ─────────────────────────────────────────────────────────────────────────
// "Is It Income or Is It Leaks?" (index engine, 0 to 100, high = leaks)
//
// Seven questions. A high score means spending behavior is the
// constraint; a low score means the ship is tight and the ceiling is the
// paycheck. The result names WHICH side to work on, never a verdict on
// the person.
// ─────────────────────────────────────────────────────────────────────────

import type { IndexQuizContent } from '../DiscoveryTypes';

export const INCOME_OR_LEAKS: IndexQuizContent = {
	meta: {
		id: 'income-or-leaks',
		title: 'Is It Income or Is It Leaks?',
		hook: 'You make decent money but retirement seems just as far as it did when you started.',
		engine: 'index',
		minutes: 3,
		status: 'live',
	},

	questions: [
		{
			type: 'index',
			id: 'il-tracking',
			prompt: 'How closely do you track your spending?',
			options: [
				{ label: 'I do not.', points: 10 },
				{ label: 'I glance at the card statement.', points: 7 },
				{ label: 'I review it monthly, roughly.', points: 3 },
				{ label: 'Every dollar has a job.', points: 0 },
			],
		},
		{
			type: 'index',
			id: 'il-month-end',
			prompt: 'What is usually left at the end of the month?',
			options: [
				{ label: 'Zero or negative, and I earn decently.', points: 10 },
				{ label: 'A little. It varies a lot.', points: 6 },
				{ label: 'A steady chunk.', points: 2 },
				{
					label: 'A fixed slice leaves for savings before I see it.',
					points: 0,
				},
			],
		},
		{
			type: 'index',
			id: 'il-subscriptions',
			prompt: 'Your recurring charges (subscriptions, memberships, auto-renewals). Could you name them from memory?',
			options: [
				{ label: 'No idea. Double digits, probably.', points: 10 },
				{ label: 'Most of them. Some would surprise me.', points: 6 },
				{ label: 'All of them.', points: 2 },
				{
					label: 'All of them, and I audited the list this year.',
					points: 0,
				},
			],
		},
		{
			type: 'index',
			id: 'il-impulse',
			prompt: 'Think of your last three "it was on sale" purchases. Where are they now?',
			options: [
				{ label: 'Still in the packaging.', points: 8 },
				{ label: 'Used a few times.', points: 5 },
				{ label: 'Genuinely in use.', points: 2 },
				{ label: 'I do not buy on sale-impulse.', points: 0 },
			],
		},
		{
			type: 'index',
			id: 'il-big-two',
			prompt: 'Housing plus cars, as a share of your take-home pay:',
			options: [
				{ label: 'Over 60%', points: 10 },
				{ label: '45% to 60%', points: 7 },
				{ label: '30% to 45%', points: 3 },
				{ label: 'Under 30%', points: 0 },
			],
		},
		{
			type: 'index',
			id: 'il-raise',
			prompt: 'When your pay goes up, what happens within a few months?',
			options: [
				{ label: 'Spending quietly absorbs all of it.', points: 10 },
				{ label: 'Some gets absorbed, some gets saved.', points: 5 },
				{
					label: 'Lifestyle stays put. Savings take the raise.',
					points: 0,
				},
			],
		},
		{
			type: 'index',
			id: 'il-trajectory',
			prompt: 'Your income over the last three years, next to your savings:',
			options: [
				{ label: 'Income grew. Savings did not move.', points: 10 },
				{ label: 'Both roughly flat.', points: 4 },
				{ label: 'Income grew and savings grew with it.', points: 0 },
			],
		},
	],

	bands: [
		{
			min: 70,
			label: 'It is leaks',
			insight:
				'The money is coming in. It is going out through holes nobody is watching. The tell is the raise question: when income rises and savings do not, spending is expanding to fill the space. The good news is that leaks are the fixable side, because every leak you close pays you every single month afterward.',
		},
		{
			min: 45,
			label: 'Both valves are open',
			insight:
				'Some of it is the paycheck and some of it is the plumbing. In this range the order matters: closing the leaks first is usually faster, because a $200 monthly leak closed today does the work of a $200 raise without the negotiation. Then the income side sets the ceiling.',
		},
		{
			min: 20,
			label: 'Mostly income',
			insight:
				'Your habits are reasonably tight. If the pile still is not growing the way you want, the constraint is the paycheck, not the plumbing. No spreadsheet fixes a paycheck. The levers on this side are skills, job changes, and negotiation, and they compound just like investments do.',
		},
		{
			min: 0,
			label: 'It is income',
			insight:
				'The ship is tight. Tracking, buffers, automation, all in place. At this score, more optimization returns almost nothing, because there is nothing left to optimize. Every future dollar of progress on your timeline now comes from earning more, and that is a different game with different rules.',
		},
	],

	cta: {
		label: 'See which lever moves your date',
		to: '/simulate',
		blurb: 'Earning more and spending less both move the date work becomes optional, but rarely by the same amount. Nisk runs both levers on your actual numbers and shows which one wins for you.',
	},

	shareText:
		'I took Nisk\u2019s Income or Leaks test and scored {score}/100 toward leaks. Most people fix the wrong side first. Find your side: https://nisk.co/quizzes/income-or-leaks',
};
